/* Web Push helpers — subscribe/unsubscribe + status via the service worker.
 * Browser support detected at call time; degrades silently elsewhere.
 */
import api from '@/lib/api';

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
}

export function isPushSupported() {
    return (
        typeof window !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window
    );
}

export function permission() {
    if (!isPushSupported()) return 'unsupported';
    return Notification.permission; // 'default' | 'granted' | 'denied'
}

export async function getRegistration() {
    if (!isPushSupported()) return null;
    return navigator.serviceWorker.ready;
}

export async function fetchStatus() {
    try {
        const { data } = await api.get('/push/status');
        return data;
    } catch {
        return { enabled: false, devices: 0, public_key: '' };
    }
}

export async function subscribe() {
    if (!isPushSupported()) throw new Error('Push not supported');
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') throw new Error('Permission ' + perm);

    const { public_key } = await fetchStatus();
    if (!public_key) throw new Error('Server VAPID key unavailable');

    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(public_key),
        });
    }
    const json = sub.toJSON();
    await api.post('/push/subscribe', {
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: navigator.userAgent,
    });
    return true;
}

export async function unsubscribe() {
    if (!isPushSupported()) return;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
        try {
            await api.delete('/push/subscribe', { params: { endpoint: sub.endpoint } });
        } catch {}
        await sub.unsubscribe();
    }
}

export async function sendTest() {
    const { data } = await api.post('/push/test');
    return data;
}
